//
// satellites-fallback.ts — a sky that is never empty
//
// Translation of `SatelliteService.fallbackCatalog()` from the iOS app,
// which is itself the fallback array the original Fuser server carried.
//
// When it gets used, and why it has to exist: CelesTrak enforces its
// two-hour publication cadence by refusing repeat downloads, so there are
// real windows — a cold cache just after someone else fetched, a server
// restart, a proxy outage — where the live catalogue is simply unavailable
// for a while. That happened during this build: purging the feed cache
// threw away a good copy, and CelesTrak would not hand back another for the
// rest of its window.
//
// The honest difference between these objects and the real ones: **they
// have no `elements`**, so `positionOf` falls back to the original Fuser
// model — a circular orbit whose phase is seeded from the NORAD ID. Their
// altitudes, inclinations and periods are correct; where they appear over
// the Earth at any given second is not. That is a deliberate trade (a
// plausible sky beats a blank one), and it is why `dataSource` says
// "offline catalog" rather than naming a live feed. Nothing here should
// ever read as tracked.
//

import type { TrackedObject } from "../types";

export function fallbackSatellites(): TrackedObject[] {
  const catalog: TrackedObject[] = [
    {
      id: "sat-25544", noradId: 25544, name: "ISS (ZARYA) — International Space Station",
      category: "active", layer: "satStations" as const, typeLabel: "CREWED SPACE STATION",
      latitude: 28.4, longitude: -80.5, altitudeKm: 418.5, velocityKms: 7.66,
      orbitalPeriodMin: 92.9, inclinationDeg: 51.6,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    },
    {
      id: "sat-20580", noradId: 20580, name: "HST — Hubble Space Telescope",
      category: "active", layer: "satStations" as const, typeLabel: "ASTRONOMICAL OBSERVATORY",
      latitude: 12.1, longitude: -45.2, altitudeKm: 535.0, velocityKms: 7.59,
      orbitalPeriodMin: 95.4, inclinationDeg: 28.5,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    },
    {
      id: "sat-43226", noradId: 43226, name: "GOES-16 (GOES-EAST)",
      category: "active", layer: "satStations" as const, typeLabel: "GEOSTATIONARY WEATHER SAT",
      latitude: 0.0, longitude: -75.2, altitudeKm: 35786.0, velocityKms: 3.07,
      orbitalPeriodMin: 1436.0, inclinationDeg: 0.05,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    },
    {
      id: "sat-43013", noradId: 43013, name: "NOAA-20 (JPSS-1)",
      category: "active", layer: "satStations" as const, typeLabel: "POLAR METEOROLOGICAL SAT",
      latitude: 62.4, longitude: 15.8, altitudeKm: 824.0, velocityKms: 7.43,
      orbitalPeriodMin: 101.2, inclinationDeg: 98.7,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    },
    {
      id: "sat-40019", noradId: 40019, name: "NAVSTAR 71 (GPS IIF-6)",
      category: "active", layer: "satStations" as const, typeLabel: "GLOBAL POSITIONING SYSTEM",
      latitude: -32.1, longitude: 115.4, altitudeKm: 20180.0, velocityKms: 3.87,
      orbitalPeriodMin: 718.0, inclinationDeg: 55.1,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    },
    {
      id: "sat-48274", noradId: 48274, name: "TIANGONG SPACE STATION",
      category: "active", layer: "satStations" as const, typeLabel: "CREWED SPACE STATION",
      latitude: -18.2, longitude: 102.5, altitudeKm: 389.0, velocityKms: 7.68,
      orbitalPeriodMin: 92.2, inclinationDeg: 41.5,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    },
    {
      id: "sat-41866", noradId: 41866, name: "SENTINEL-2B",
      category: "active", layer: "satStations" as const, typeLabel: "COPERNICUS EARTH OBSERVATION",
      latitude: 45.2, longitude: 8.5, altitudeKm: 786.0, velocityKms: 7.45,
      orbitalPeriodMin: 100.6, inclinationDeg: 98.6,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    },
  ];

  // A sample constellation, so the Starlink layer is not simply blank. A
  // constellation only reads as a constellation in numbers.
  for (let i = 0; i < 24; i++) {
    const noradId = 31000 + i * 15;
    catalog.push({
      id: `sat-starlink-${noradId}`, noradId, name: `STARLINK-${noradId}`,
      category: "starlink", layer: "satStarlink" as const, typeLabel: "STARLINK V1.5 BROADBAND",
      latitude: Math.sin(i * 0.8) * 53,
      longitude: ((i * 20 - 150) % 360),
      altitudeKm: 550.0, velocityKms: 7.59,
      orbitalPeriodMin: 95.1, inclinationDeg: 53.0,
      dataSource: "AZISUR offline catalog", lastUpdated: "Approximate position",
    });
  }

  return catalog;
}
